import React, { useState, useEffect, useRef, useCallback } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import PhotoCaptureModalEnhanced from './PhotoCaptureModalEnhanced';
import {
  X, Camera, Upload, Monitor, RefreshCw, Check,
  ChevronDown, ZoomIn, Wifi, RotateCw, ArrowLeft,
  Minus, Plus, Clipboard,
} from 'lucide-react';
import { PhotoResult } from './PhotoUploadModal';
import { getCroppedImg, padImageWithBlack, dataUrlToPhotoOriginal } from '../utils/imageUtils';
import { api } from '../services/api';
import { cn } from '../utils/cn';
import { MjpegLivePreview } from './MjpegLivePreview';
import { mjpegRegistry } from '../utils/mjpegStreamRegistry';
import { AllPhotosStreamTracker } from './allPhotosStreamTracker';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CameraCategory {
  id: string;
  name: string;
  display_name?: string;
}

interface RegistrationCamera {
  id: string;
  name: string;
  ip_address: string;
  port: number;
  protocol: 'rtsp' | 'http' | 'mjpeg';
  username?: string;
  password?: string;
  snapshot_path?: string;
  rtsp_path?: string;
  categories?: CameraCategory[];
}

type SourceOption =
  | { kind: 'none' }
  | { kind: 'webcam'; deviceId: string; label: string }
  | { kind: 'ipcamera'; camera: RegistrationCamera };

// A named preset bundling a registration camera per fixed photo slot — configured
// on the "Câmeras de Cadastro" admin page, applied here with one click.
interface CameraSet {
  id: string;
  name: string;
  identity_camera_id?: string | null;
  document_front_camera_id?: string | null;
  document_back_camera_id?: string | null;
  vehicle_camera_id?: string | null;
}

// Maps a fixed column type to the CameraSet field holding its camera id.
// Columns outside this fixed set (custom columns passed via the `columns` prop)
// simply have no preset mapping and are left untouched by applyPreset.
const PRESET_SLOT_FIELD: Record<string, keyof CameraSet> = {
  identity: 'identity_camera_id',
  document_front: 'document_front_camera_id',
  document_back: 'document_back_camera_id',
  vehicle: 'vehicle_camera_id',
};

export interface PhotoColumn {
  type: string;
  label: string;
  /** 'circle' → round crop, 1:1 aspect  |  'rect' → rectangular crop, 3:2 aspect */
  shape?: 'circle' | 'rect';
}

interface EditingState {
  colType: string;
  imageSrc: string;                   // padded data URL
  rawImageSrc: string;                // unpadded source, kept so the original survives the crop
  crop: { x: number; y: number };
  zoom: number;
  rotation: number;
  croppedAreaPixels: Area | null;
  bgColor: '#000000' | '#ffffff';
  isProcessing: boolean;
}

interface AllPhotosEditModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (photos: Partial<Record<string, PhotoResult>>) => void;
  currentPhotos?: Partial<Record<string, PhotoResult | null>>;
  customerId?: string;
  darkMode?: boolean;
  columns?: PhotoColumn[];
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_COLUMNS: PhotoColumn[] = [
  { type: 'identity',       label: 'Identidade',  shape: 'circle' },
  { type: 'document_front', label: 'Doc. Frente',  shape: 'rect'   },
  { type: 'document_back',  label: 'Doc. Verso',   shape: 'rect'   },
  { type: 'vehicle',        label: 'Veículo',       shape: 'rect'   },
];

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 8;
const ZOOM_INITIAL = 2; // fills after padding

function colAspect(col: PhotoColumn) { return col.shape === 'circle' ? 4 / 5 : 3 / 2; }
function colCropShape(col: PhotoColumn): 'round' | 'rect' { return col.shape === 'circle' ? 'round' : 'rect'; }

// Camera category filtering:
// - identity cols → cameras tagged captura_facial or visitante
// - document cols → cameras tagged documento
// - untagged cameras (no categories) → show in all columns
// - vehicle and other cols → show all
const IDENTITY_CATS = ['captura_facial', 'visitante'];
const DOCUMENT_CATS = ['documento'];

function getCamerasForColumn(cameras: RegistrationCamera[], colType: string): RegistrationCamera[] {
  return cameras.filter(cam => {
    const cats = cam.categories ?? [];
    if (cats.length === 0) return true; // untagged → show everywhere
    if (colType === 'identity') return cats.some(c => IDENTITY_CATS.includes(c.name));
    if (colType === 'document_front' || colType === 'document_back') return cats.some(c => DOCUMENT_CATS.includes(c.name));
    return true;
  });
}


/**
 * Build MJPEG streaming URL — MUST produce the identical string as
 * VisitorRegistrationModal.buildMjpegUrl for the same camera so the
 * mjpegRegistry deduplicates to ONE <img> / ONE HTTP connection.
 * Key rule: omit port 80 (http) and 443 (https) — standard ports.
 */
function buildMjpegUrl(cam: RegistrationCamera): string {
  const scheme = cam.port === 443 ? 'https' : 'http';
  const rawIp = cam.ip_address.replace(/^(https?:\/\/|rtsp:\/\/)/i, '');
  const sp = cam.snapshot_path || '';
  const mjpegPath = sp.includes('/picture')
    ? sp.replace('/picture', '/httppreview')
    : '/ISAPI/Streaming/channels/101/httppreview';
  const auth = cam.username
    ? `${encodeURIComponent(cam.username)}${cam.password ? `:${encodeURIComponent(cam.password)}` : ''}@`
    : '';
  const portStr = (scheme === 'http' && cam.port !== 80) || (scheme === 'https' && cam.port !== 443)
    ? `:${cam.port}`
    : '';
  return `${scheme}://${auth}${rawIp}${portStr}${mjpegPath}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Has anything in the modal changed since it opened?
 *
 * Compared by data URL rather than by object identity: a capture produces a NEW object for the
 * same image on every re-render, so identity would call an untouched modal dirty and ask the
 * operator to confirm a discard they never made.
 */
/** Anything that carries a dataUrl — PhotoResult, or a bare `{ dataUrl }` in a test. */
type PhotoLike = { dataUrl?: string } | null | undefined;

export function hasUnsavedPhotoEdits(
  captured: Record<string, any>,
  openedWith: Record<string, any>,
): boolean {
  const read = (value: PhotoLike) => value?.dataUrl ?? null;
  const types = new Set([...Object.keys(captured ?? {}), ...Object.keys(openedWith ?? {})]);
  for (const type of Array.from(types)) {
    if (read(captured?.[type]) !== read(openedWith?.[type])) return true;
  }
  return false;
}

export const AllPhotosEditModal: React.FC<AllPhotosEditModalProps> = ({
  open,
  onClose,
  onSave,
  currentPhotos = {},
  customerId,
  darkMode = false,
  columns = DEFAULT_COLUMNS,
}) => {
  const isElectron = typeof window !== 'undefined' && !!window.electronAPI;

  // ── Per-column state ──────────────────────────────────────────────────
  const [sources, setSources] = useState<Record<string, SourceOption>>(() =>
    Object.fromEntries(columns.map(c => [c.type, { kind: 'none' } as SourceOption]))
  );
  const [captured, setCaptured] = useState<Record<string, PhotoResult | null>>(() =>
    Object.fromEntries(columns.map(c => [c.type, currentPhotos[c.type] ?? null]))
  );
  const [ipPreviews, setIpPreviews] = useState<Record<string, string | null>>({});

  // ── Editing overlay state (null = columns view) ───────────────────────
  const [editing, setEditing] = useState<EditingState | null>(null);

  // ── Device lists ──────────────────────────────────────────────────────
  const [webcamDevices, setWebcamDevices] = useState<MediaDeviceInfo[]>([]);
  const [ipCameras, setIpCameras] = useState<RegistrationCamera[]>([]);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [camerasLoaded, setCamerasLoaded] = useState(false);
  const restoredRef = useRef(false);

  // ── Camera set presets ───────────────────────────────────────────────
  const [cameraSets, setCameraSets] = useState<CameraSet[]>([]);
  const [cameraSetsLoaded, setCameraSetsLoaded] = useState(false);
  const [appliedPresetId, setAppliedPresetId] = useState<string | null>(null);
  const LAST_PRESET_KEY = customerId ? `allPhotos_lastPreset_${customerId}` : null;

  // ── Refs ──────────────────────────────────────────────────────────────
  const videoRefs        = useRef<Map<string, HTMLVideoElement | null>>(new Map());
  // Stable cache of real HTMLVideoElement objects — React nulls videoRefs before
  // effects run, so we keep our own reference for cleanup (pause + srcObject=null).
  const videoElCache     = useRef<Map<string, HTMLVideoElement>>(new Map());
  const streamMap        = useRef<Map<string, MediaStream>>(new Map());
  // Owns every RTSP stream this modal starts. Its columns are the only streams
  // allowed past the main process's concurrency cap and same-camera dedup kill,
  // so the tracker — not the main process — is what guarantees they all die.
  // Lazily constructed: this component re-renders on every incoming video frame.
  const streams          = useRef<AllPhotosStreamTracker>();
  if (!streams.current) streams.current = new AllPhotosStreamTracker(() => window.electronAPI);
  const httpTimers       = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());
  const fileInputs       = useRef<Map<string, HTMLInputElement | null>>(new Map());
  // Prevents stale startWebcam callbacks from running after modal closes
  const isTearingDownRef = useRef(false);

  // ── UI state ──────────────────────────────────────────────────────────
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [zoomUrl,      setZoomUrl]      = useState<string | null>(null);

  // Close dropdown when clicking anywhere outside (avoids z-index / stacking-context conflicts)
  useEffect(() => {
    if (!dropdownOpen) return;
    const close = () => setDropdownOpen(null);
    // rAF skips the very click that opened the dropdown
    const raf = requestAnimationFrame(() => document.addEventListener('click', close, { once: true }));
    return () => { cancelAnimationFrame(raf); document.removeEventListener('click', close); };
  }, [dropdownOpen]);

  // ─────────────────────────────────────────────────────────────────────
  // Enumerate webcams
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    navigator.mediaDevices?.enumerateDevices()
      .then(d => { setWebcamDevices(d.filter(x => x.kind === 'videoinput')); setDevicesLoaded(true); })
      .catch(() => setDevicesLoaded(true));
  }, [open]);

  // ─────────────────────────────────────────────────────────────────────
  // Load IP cameras
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (!customerId) { setCamerasLoaded(true); return; }
    api.get(`/registration-cameras?customer_id=${customerId}`)
      .then(r => { setIpCameras(r.data?.cameras ?? r.data ?? []); setCamerasLoaded(true); })
      .catch(() => setCamerasLoaded(true));
  }, [open, customerId]);

  // ─────────────────────────────────────────────────────────────────────
  // Load camera set presets
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (!customerId) { setCameraSetsLoaded(true); return; }
    api.get(`/camera-sets?customer_id=${customerId}`)
      .then(r => { setCameraSets(r.data?.sets ?? r.data ?? []); setCameraSetsLoaded(true); })
      .catch(() => setCameraSetsLoaded(true));
  }, [open, customerId]);

  // ─────────────────────────────────────────────────────────────────────
  // Global RTSP frame listener
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isElectron || !open) return;
    const remove = window.electronAPI?.onIPCameraFrame?.((data: { streamId: string; frame: string }) => {
      // Frames from a superseded stream resolve to no column and are dropped.
      // Several columns can share one stream (same camera) — all of them update.
      const cols = streams.current?.columnsFor(data.streamId) ?? [];
      if (cols.length === 0) return;
      setIpPreviews(p => {
        const next = { ...p };
        cols.forEach(colType => { next[colType] = data.frame; });
        return next;
      });
    });
    return () => { remove?.(); };
  }, [isElectron, open]);

  // ─────────────────────────────────────────────────────────────────────
  // A stream the main process gave up on (ffmpeg died, or it was evicted).
  // Forget the id — otherwise keepalive keeps pinging a ghost and the column
  // keeps showing the last frame under a live badge. Reconnects do NOT come
  // through here (the entry stays in the main process map), so a column only
  // resets when its stream is really gone.
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isElectron || !open) return;
    const remove = window.electronAPI?.onIPCameraStreamEnded?.((data: { streamId: string }) => {
      const cols = streams.current?.forget(data.streamId) ?? [];
      if (cols.length === 0) return;
      setIpPreviews(p => {
        const next = { ...p };
        cols.forEach(colType => { next[colType] = null; });
        return next;
      });
    });
    return () => { remove?.(); };
  }, [isElectron, open]);

  // ─────────────────────────────────────────────────────────────────────
  // RTSP keepalive — Electron kills streams after 12 s without a ping
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isElectron || !open) return;
    const timer = setInterval(() => streams.current?.keepalive(), 3000);
    return () => clearInterval(timer);
  }, [isElectron, open]);

  // ─────────────────────────────────────────────────────────────────────
  // Webcam helpers
  // ─────────────────────────────────────────────────────────────────────
  const startWebcam = useCallback(async (colType: string, deviceId: string) => {
    if (isTearingDownRef.current) return;
    try {
      let stream = streamMap.current.get(deviceId);
      if (!stream || !stream.active) {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        streamMap.current.set(deviceId, stream);
      }
      if (isTearingDownRef.current) {
        // Modal closed while getUserMedia was pending — stop the fresh stream immediately
        stream.getTracks().forEach(t => t.stop());
        streamMap.current.delete(deviceId);
        return;
      }
      // Use videoElCache as the reliable reference (videoRefs can be null mid-render)
      const video = videoElCache.current.get(colType) ?? videoRefs.current.get(colType);
      if (video) { video.srcObject = stream; video.play().catch(() => {}); }
    } catch (err) {
      console.warn('[AllPhotosEditModal] getUserMedia error:', err);
    }
  }, []);

  const releaseWebcamIfUnused = useCallback((deviceId: string, except: string, curSources: Record<string, SourceOption>) => {
    const inUse = Object.entries(curSources).some(([col, src]) =>
      col !== except && src.kind === 'webcam' && (src as Extract<SourceOption, { kind: 'webcam' }>).deviceId === deviceId
    );
    if (!inUse) { streamMap.current.get(deviceId)?.getTracks().forEach(t => t.stop()); streamMap.current.delete(deviceId); }
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // IP camera helpers
  // ─────────────────────────────────────────────────────────────────────
  const startIPCamera = useCallback((colType: string, cam: RegistrationCamera) => {
    // HTTP/MJPEG cameras are served by <MjpegLivePreview> via the registry.
    // The registry maintains ONE hidden <img> per URL, shared among all subscribers,
    // so MONITORAMENTO (VisitorRegistrationModal) and ROSTO (this modal) on the same
    // physical camera use a SINGLE HTTP connection — camera never gets a second request.
    // RTSP goes through the tracker, which tags the stream so the main process lets
    // all four columns run alongside MONITORAMENTO (see allPhotosStreamTracker.ts).
    streams.current?.start(colType, cam);
  }, []);

  const stopIPCamera = useCallback((colType: string) => {
    streams.current?.stop(colType);
    const t = httpTimers.current.get(colType);
    if (t) { clearInterval(t); httpTimers.current.delete(colType); }
    setIpPreviews(p => ({ ...p, [colType]: null }));
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // Source change
  // ─────────────────────────────────────────────────────────────────────
  const LS_KEY = (colType: string) => `allPhotos_src_${colType}`;

  const handleSourceChange = useCallback((colType: string, newSrc: SourceOption) => {
    // Prevent the localStorage auto-restore from overwriting this manual change
    restoredRef.current = true;

    const old = sources[colType];
    if (old.kind === 'webcam') {
      // Use videoElCache — more reliable than videoRefs which can be null mid-render
      const v = videoElCache.current.get(colType) ?? videoRefs.current.get(colType);
      if (v) v.srcObject = null;
      releaseWebcamIfUnused(old.deviceId, colType, sources);
    } else if (old.kind === 'ipcamera') {
      stopIPCamera(colType);
    }
    setSources(p => ({ ...p, [colType]: newSrc }));
    setDropdownOpen(null);

    // Persist selection
    if (newSrc.kind === 'none') {
      localStorage.removeItem(LS_KEY(colType));
    } else if (newSrc.kind === 'webcam') {
      localStorage.setItem(LS_KEY(colType), JSON.stringify({ kind: 'webcam', deviceId: newSrc.deviceId, label: newSrc.label }));
    } else if (newSrc.kind === 'ipcamera') {
      localStorage.setItem(LS_KEY(colType), JSON.stringify({ kind: 'ipcamera', cameraId: newSrc.camera.id }));
    }

    if (newSrc.kind === 'webcam') setTimeout(() => startWebcam(colType, newSrc.deviceId), 80);
    else if (newSrc.kind === 'ipcamera') startIPCamera(colType, newSrc.camera);
  }, [sources, releaseWebcamIfUnused, stopIPCamera, startWebcam, startIPCamera]);

  // ─────────────────────────────────────────────────────────────────────
  // Apply a camera set preset — sets every fixed slot it defines via the same
  // handleSourceChange path a manual per-slot pick would use (so persistence /
  // stream start/stop all stay consistent). Returns the column types it touched
  // so the caller can fall back to per-column memory for the ones it left unset.
  // ─────────────────────────────────────────────────────────────────────
  const applyPreset = useCallback((preset: CameraSet, persist: boolean = true): Set<string> => {
    const applied = new Set<string>();
    for (const col of columns) {
      const field = PRESET_SLOT_FIELD[col.type];
      if (!field) continue;
      const camId = preset[field] as string | null | undefined;
      if (!camId) continue;
      const cam = ipCameras.find(c => c.id === camId);
      if (!cam) continue;
      handleSourceChange(col.type, { kind: 'ipcamera', camera: cam });
      applied.add(col.type);
    }
    setAppliedPresetId(preset.id);
    if (persist && LAST_PRESET_KEY) localStorage.setItem(LAST_PRESET_KEY, preset.id);
    return applied;
  }, [columns, ipCameras, handleSourceChange, LAST_PRESET_KEY]);

  // ─────────────────────────────────────────────────────────────────────
  // Restore last camera selection from localStorage (once, after load)
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !devicesLoaded || !camerasLoaded || !cameraSetsLoaded || restoredRef.current) return;
    restoredRef.current = true;

    // Auto-apply whichever preset was used last time, if it still exists —
    // the slots it defines take priority over per-slot memory below.
    let presetAppliedCols = new Set<string>();
    if (LAST_PRESET_KEY) {
      const lastPresetId = localStorage.getItem(LAST_PRESET_KEY);
      const lastPreset = lastPresetId ? cameraSets.find(s => s.id === lastPresetId) : undefined;
      if (lastPreset) presetAppliedCols = applyPreset(lastPreset, false);
    }

    const restored: Record<string, SourceOption> = {};
    const toStart: Array<{ colType: string; src: SourceOption }> = [];

    for (const col of columns) {
      if (presetAppliedCols.has(col.type)) continue; // preset already covered this slot
      const raw = localStorage.getItem(`allPhotos_src_${col.type}`);
      if (!raw) continue;
      try {
        const p = JSON.parse(raw);
        if (p.kind === 'webcam') {
          const dev = webcamDevices.find(d => d.deviceId === p.deviceId);
          if (dev) {
            const src: SourceOption = { kind: 'webcam', deviceId: dev.deviceId, label: dev.label || p.label || 'Câmera' };
            restored[col.type] = src;
            toStart.push({ colType: col.type, src });
          }
        } else if (p.kind === 'ipcamera') {
          const cam = ipCameras.find(c => c.id === p.cameraId);
          if (cam) {
            const src: SourceOption = { kind: 'ipcamera', camera: cam };
            restored[col.type] = src;
            toStart.push({ colType: col.type, src });
          }
        }
      } catch {}
    }

    if (Object.keys(restored).length > 0) {
      setSources(p => ({ ...p, ...restored }));
      setTimeout(() => {
        toStart.forEach(({ colType, src }) => {
          if (src.kind === 'webcam') startWebcam(colType, (src as Extract<SourceOption,{kind:'webcam'}>).deviceId);
          else if (src.kind === 'ipcamera') startIPCamera(colType, (src as Extract<SourceOption,{kind:'ipcamera'}>).camera);
        });
      }, 120);
    }
  }, [open, devicesLoaded, camerasLoaded, cameraSetsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────
  // Open crop editor with a raw image data URL
  // ─────────────────────────────────────────────────────────────────────
  const openEditor = useCallback(async (colType: string, rawDataUrl: string) => {
    const padded = await padImageWithBlack(rawDataUrl, 0.5);
    setEditing({
      colType,
      imageSrc: padded,
      rawImageSrc: rawDataUrl,
      crop: { x: 0, y: 0 },
      zoom: ZOOM_INITIAL,
      rotation: 0,
      croppedAreaPixels: null,
      bgColor: '#000000',
      isProcessing: false,
    });
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // Capture from live preview → set directly (no editor step)
  // ─────────────────────────────────────────────────────────────────────
  const capturePreview = useCallback(async (colType: string) => {
    const src = sources[colType];

    if (src.kind === 'webcam') {
      const video = videoRefs.current.get(colType);
      if (!video?.videoWidth) return;
      const c = document.createElement('canvas');
      c.width = video.videoWidth; c.height = video.videoHeight;
      c.getContext('2d')?.drawImage(video, 0, 0);
      c.toBlob(blob => {
        if (!blob) return;
        setCaptured(p => ({
          ...p,
          [colType]: { blob, base64: c.toDataURL('image/jpeg', 0.95), width: c.width, height: c.height, faceDetected: false },
        }));
      }, 'image/jpeg', 0.95);
    } else if (src.kind === 'ipcamera') {
      const cam = (src as Extract<SourceOption, { kind: 'ipcamera' }>).camera;
      if (cam.protocol === 'rtsp') {
        // RTSP: last frame from Electron IPC
        const dataUrl = ipPreviews[colType];
        if (!dataUrl) return;
        try {
          const res = await fetch(dataUrl);
          const blob = await res.blob();
          setCaptured(p => ({
            ...p,
            [colType]: { blob, base64: dataUrl, width: 0, height: 0, faceDetected: false },
          }));
        } catch {}
      } else {
        // HTTP/MJPEG: last frame from the shared registry canvas (no extra camera connection)
        const url = buildMjpegUrl(cam);
        const lastFrame = mjpegRegistry.getLastFrame(url);
        if (!lastFrame) return;
        try {
          const res = await fetch(lastFrame);
          const blob = await res.blob();
          setCaptured(p => ({
            ...p,
            [colType]: { blob, base64: lastFrame, width: 0, height: 0, faceDetected: false },
          }));
        } catch {}
      }
    }
  }, [sources, ipPreviews]);

  // ─────────────────────────────────────────────────────────────────────
  // File upload → set directly (no editor step)
  // ─────────────────────────────────────────────────────────────────────
  const handleFileChange = useCallback((colType: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const base64 = ev.target?.result as string;
      const img = new Image();
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = img.width; c.height = img.height;
        c.getContext('2d')?.drawImage(img, 0, 0);
        c.toBlob(blob => {
          if (!blob) return;
          setCaptured(p => ({
            ...p,
            [colType]: { blob, base64, width: img.width, height: img.height, faceDetected: false },
          }));
        }, 'image/jpeg', 0.95);
      };
      img.src = base64;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // Clipboard paste → set directly
  // ─────────────────────────────────────────────────────────────────────
  const handlePaste = useCallback(async (colType: string) => {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        for (const type of item.types) {
          if (type.startsWith('image/')) {
            const blob = await item.getType(type);
            const base64 = await new Promise<string>(resolve => {
              const reader = new FileReader();
              reader.onload = e => resolve(e.target?.result as string);
              reader.readAsDataURL(blob);
            });
            setCaptured(p => ({
              ...p,
              [colType]: { blob, base64, width: 0, height: 0, faceDetected: false },
            }));
            return;
          }
        }
      }
    } catch (err) {
      console.warn('[AllPhotosEditModal] paste error:', err);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // Crop confirm
  // ─────────────────────────────────────────────────────────────────────
  const handleCropConfirm = useCallback(async () => {
    if (!editing?.croppedAreaPixels) return;
    const col = columns.find(c => c.type === editing.colType)!;
    const isCircle = col.shape === 'circle';
    const outputSize = isCircle ? { width: 800, height: 1000 } : { width: 1200, height: 800 };

    setEditing(e => e ? { ...e, isProcessing: true } : e);
    try {
      const result = await getCroppedImg(
        editing.imageSrc,
        editing.croppedAreaPixels,
        editing.rotation,
        outputSize,
        { horizontal: false, vertical: false },
        editing.bgColor,
      );
      setCaptured(p => ({
        ...p,
        [editing.colType]: {
          blob: result.blob,
          base64: result.base64,
          width: result.width,
          height: result.height,
          faceDetected: false,
          // Only the identity column's original is ever uploaded (see appendIdentityPhoto),
          // but it is carried for every column so the crop can be redone in-session.
          original: dataUrlToPhotoOriginal(editing.rawImageSrc),
        },
      }));
      setEditing(null);
    } catch (err) {
      console.error('[AllPhotosEditModal] crop error:', err);
      setEditing(e => e ? { ...e, isProcessing: false } : e);
    }
  }, [editing, columns]);

  // ─────────────────────────────────────────────────────────────────────
  // Core teardown helper — called both on close and on unmount
  // ─────────────────────────────────────────────────────────────────────
  const teardownAll = useCallback(() => {
    isTearingDownRef.current = true;

    // 1. Pause every video element and clear srcObject so the OS camera
    //    indicator turns off immediately. videoElCache survives after React
    //    nulls videoRefs, because it holds direct JS references.
    videoElCache.current.forEach(video => {
      try { video.pause(); } catch {}
      video.srcObject = null;
    });
    videoElCache.current.clear();

    // 2. Stop all USB/webcam MediaStreamTracks
    streamMap.current.forEach(s => s.getTracks().forEach(t => t.stop()));
    streamMap.current.clear();

    // 3. Stop IP camera HTTP polling
    httpTimers.current.forEach(t => clearInterval(t));
    httpTimers.current.clear();

    // 4. Stop every RTSP stream this modal started, then group-stop as a backstop
    //    for ids it may have lost (a start that resolved after close, a remount).
    //    The tracker also refuses to start anything until the modal reopens.
    streams.current?.teardown();
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // Full cleanup on close
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) return;
    teardownAll();
    setIpPreviews({});
    setEditing(null);
    setDevicesLoaded(false);
    setCamerasLoaded(false);
    setCameraSetsLoaded(false);
    setAppliedPresetId(null);
    restoredRef.current = false;
    setSources(Object.fromEntries(columns.map(c => [c.type, { kind: 'none' } as SourceOption])));
  }, [open, columns, teardownAll]);

  // Safety net: also teardown if the component fully unmounts
  useEffect(() => () => teardownAll(), [teardownAll]);

  // Reset captured when modal opens; also clear the teardown guard so startWebcam works again
  useEffect(() => {
    if (open) {
      isTearingDownRef.current = false;
      streams.current?.open(); // re-arm after a previous teardown
      setCaptured(Object.fromEntries(columns.map(c => [c.type, currentPhotos[c.type] ?? null])));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────
  // Save all
  // ─────────────────────────────────────────────────────────────────────
  // What the modal opened with, to compare against on the way out. Captured here rather than
  // read from props at close time because `currentPhotos` is a fresh object on every render.
  const openedWithRef = useRef<Record<string, PhotoResult | null>>({});
  useEffect(() => {
    if (open) {
      openedWithRef.current = Object.fromEntries(columns.map(c => [c.type, currentPhotos[c.type] ?? null]));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  /** Close, unless there is unsaved work — then ask. */
  const requestClose = useCallback(() => {
    if (hasUnsavedPhotoEdits(captured, openedWithRef.current)) {
      setConfirmingDiscard(true);
      return;
    }
    onClose();
  }, [captured, onClose]);

  const handleSave = useCallback(() => {
    const result: Partial<Record<string, PhotoResult>> = {};
    for (const col of columns) { const p = captured[col.type]; if (p) result[col.type] = p; }
    onSave(result);
    onClose();
  }, [captured, columns, onSave, onClose]);

  if (!open) return null;

  const editingCol = editing ? columns.find(c => c.type === editing.colType) : null;

  return (
    <>
      {/* Backdrop + centering wrapper. A click out here used to close immediately, which threw
          away a face photo taken at the gate or a document the visitor is no longer holding —
          work that cannot be retyped, lost to the commonest mis-click there is. */}
      <div
        className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-2 sm:p-4"
        onClick={requestClose}
      >
      <div
        className={cn(
          'w-full max-w-5xl rounded-2xl shadow-2xl flex flex-col overflow-hidden relative',
          editing ? 'h-[90vh]' : 'max-h-[90vh]',
          darkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900',
        )}
        onClick={e => e.stopPropagation()}
      >
        {/* Rendered inside the card, not through the global dialog provider: this modal sits at
            z-[200] and a confirmation that lands underneath it is worse than none. */}
        {confirmingDiscard && (
          <div
            data-testid="photos-discard-confirm"
            className="absolute inset-0 z-10 flex items-center justify-center bg-black/50 p-4"
          >
            <div className={cn(
              'w-full max-w-sm rounded-xl border p-4 shadow-xl',
              darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200',
            )}>
              <p className="text-sm font-semibold">Descartar as fotos?</p>
              <p className={cn('text-xs mt-1', darkMode ? 'text-gray-400' : 'text-gray-500')}>
                As fotos capturadas nesta janela ainda não foram salvas e serão perdidas.
              </p>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setConfirmingDiscard(false)}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                    darkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50',
                  )}
                >
                  Continuar editando
                </button>
                <button
                  type="button"
                  onClick={() => { setConfirmingDiscard(false); onClose(); }}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  Descartar fotos
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className={cn(
          'flex items-center justify-between px-4 py-3 border-b flex-shrink-0',
          darkMode ? 'border-gray-700' : 'border-gray-200',
        )}>
          {editing ? (
            <button
              type="button"
              onClick={() => setEditing(null)}
              className={cn('flex items-center gap-1.5 text-sm transition-colors', darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900')}
            >
              <ArrowLeft className="w-4 h-4" />
              {editingCol?.label ?? 'Voltar'}
            </button>
          ) : (
            <div>
              <h2 className="text-sm font-semibold">Editar Fotos</h2>
              <p className={cn('text-[11px]', darkMode ? 'text-gray-500' : 'text-gray-400')}>
                Selecione a fonte · Clique na prévia para capturar · Edite e confirme
              </p>
            </div>
          )}
        </div>

        {/* ── Main area ──────────────────────────────────────────────── */}
        <div className={cn('min-h-0 flex flex-col overflow-hidden', editing && 'flex-1')}>

          {/* ══════════════════════════════════════════════════════════
              EDITOR SCREEN
          ══════════════════════════════════════════════════════════ */}
          {/* ══════════════════════════════════════════════════════════
              EDITOR — the shared one.
              This modal used to carry a fourth cropper of its own, so which editor an operator
              got depended on which screen they came from, and work done on the shared one never
              reached here. Capture stays above (four columns, each with camera/file/paste); only
              the frame crosses over. Each column keeps its own shape.
          ══════════════════════════════════════════════════════════ */}
          {editing && editingCol && (
            <PhotoCaptureModalEnhanced
              open
              existingPhoto={editing.rawImageSrc}
              editOnly
              onRetake={() => setEditing(null)}
              retakeLabel="Voltar"
              captureSource="file"
              title={editingCol.label}
              cropShape={colCropShape(editingCol)}
              aspectRatio={colAspect(editingCol)}
              disableFaceDetection={editingCol.shape !== 'circle'}
              darkMode={darkMode}
              hideIPCamera
              onClose={() => setEditing(null)}
              onConfirm={(result) => {
                setCaptured(p => ({ ...p, [editing.colType]: result }));
                setEditing(null);
              }}
            />
          )}

          {/* ── Preset bar — one-click apply a saved camera combination ── */}
          {!editing && cameraSets.length > 0 && (
            <div className={cn(
              'flex items-center gap-1.5 px-3 pt-3 flex-wrap flex-shrink-0',
            )}>
              <span className={cn('text-[10px] font-semibold uppercase tracking-wider mr-0.5', darkMode ? 'text-gray-500' : 'text-gray-400')}>
                Conjuntos:
              </span>
              {cameraSets.map(preset => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPreset(preset, true)}
                  className={cn(
                    'px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors',
                    appliedPresetId === preset.id
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : darkMode
                        ? 'border-gray-700 text-gray-300 hover:border-gray-500 hover:bg-gray-800'
                        : 'border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50',
                  )}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════
              COLUMNS VIEW — always mounted so video srcObject survives
          ══════════════════════════════════════════════════════════ */}
          <div className={cn('flex items-start overflow-x-auto overflow-y-auto p-3 gap-3', editing && 'hidden')}>
              {columns.map(col => {
                const source    = sources[col.type] ?? { kind: 'none' };
                const photo     = captured[col.type];
                const hasSource = source.kind !== 'none';
                const isCircle  = col.shape === 'circle';
                const ipCam        = source.kind === 'ipcamera' ? (source as Extract<SourceOption,{kind:'ipcamera'}>).camera : null;
                // HTTP/MJPEG → MjpegLivePreview (shared registry connection)
                // RTSP → Electron IPC frames in ipPreviews
                const isHttpCam    = !!ipCam && ipCam.protocol !== 'rtsp';
                const mjpegUrl     = ipCam && isHttpCam ? buildMjpegUrl(ipCam) : undefined;
                const framePreview = ipCam && !isHttpCam ? (ipPreviews[col.type] ?? undefined) : undefined;

                return (
                  <div
                    key={col.type}
                    className={cn(
                      'flex-1 min-w-[130px] flex flex-col rounded-xl border overflow-hidden',
                      darkMode ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50/80',
                    )}
                  >
                    {/* Column header */}
                    <div className={cn(
                      'px-2.5 py-1.5 border-b flex items-center justify-between flex-shrink-0',
                      darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white',
                    )}>
                      <span className="text-[10px] font-semibold uppercase tracking-wider">{col.label}</span>
                      {photo && <Check className="w-3 h-3 text-emerald-500 flex-shrink-0" />}
                    </div>

                    <div className="flex flex-col p-2 gap-2">

                      {/* ── Source selector ────────────────────────── */}
                      <div className="relative flex-shrink-0">
                        <button
                          type="button"
                          aria-label={`Fonte ${col.label}`}
                          aria-haspopup="menu"
                          aria-expanded={dropdownOpen === col.type}
                          onClick={() => setDropdownOpen(d => d === col.type ? null : col.type)}
                          className={cn(
                            'w-full flex items-center justify-between px-2 py-1 rounded-lg text-[11px] border transition-colors',
                            darkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-gray-200' : 'bg-white border-gray-300 hover:bg-gray-50 text-gray-700',
                          )}
                        >
                          <span className="flex items-center gap-1 min-w-0 truncate">
                            {source.kind === 'none' && <span className="opacity-50">Sem câmera</span>}
                            {source.kind === 'webcam' && <><Monitor className="w-2.5 h-2.5 flex-shrink-0 text-blue-400" /><span className="truncate">{(source as Extract<SourceOption,{kind:'webcam'}>).label}</span></>}
                            {source.kind === 'ipcamera' && <><Wifi className="w-2.5 h-2.5 flex-shrink-0 text-purple-400" /><span className="truncate">{(source as Extract<SourceOption,{kind:'ipcamera'}>).camera.name}</span></>}
                          </span>
                          <ChevronDown className="w-2.5 h-2.5 flex-shrink-0 ml-1 opacity-50" />
                        </button>

                        {dropdownOpen === col.type && (
                          <div role="menu" aria-label={`Fontes ${col.label}`} className={cn(
                            'absolute top-full left-0 right-0 mt-1 rounded-lg border shadow-xl z-20 py-1 max-h-48 overflow-y-auto',
                            darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200',
                          )}>
                            <DropdownItem darkMode={darkMode} active={source.kind === 'none'} onClick={() => handleSourceChange(col.type, { kind: 'none' })}>
                              <span className="opacity-50 text-[11px]">Sem câmera</span>
                            </DropdownItem>
                            {webcamDevices.length > 0 && <>
                              <DropdownSection darkMode={darkMode}>USB / Integrada</DropdownSection>
                              {webcamDevices.map((dev, i) => (
                                <DropdownItem
                                  key={dev.deviceId}
                                  darkMode={darkMode}
                                  active={source.kind === 'webcam' && (source as Extract<SourceOption,{kind:'webcam'}>).deviceId === dev.deviceId}
                                  onClick={() => handleSourceChange(col.type, { kind: 'webcam', deviceId: dev.deviceId, label: dev.label || `Câmera ${i + 1}` })}
                                >
                                  <Monitor className="w-2.5 h-2.5 flex-shrink-0 text-blue-400" />
                                  <span className="text-[11px] truncate">{dev.label || `Câmera ${i + 1}`}</span>
                                </DropdownItem>
                              ))}
                            </>}
                            {(() => {
                              const filteredCams = getCamerasForColumn(ipCameras, col.type);
                              return filteredCams.length > 0 ? (
                                <>
                                  <DropdownSection darkMode={darkMode}>IP / RTSP</DropdownSection>
                                  {filteredCams.map(cam => (
                                    <DropdownItem
                                      key={cam.id}
                                      darkMode={darkMode}
                                      active={source.kind === 'ipcamera' && (source as Extract<SourceOption,{kind:'ipcamera'}>).camera.id === cam.id}
                                      onClick={() => handleSourceChange(col.type, { kind: 'ipcamera', camera: cam })}
                                    >
                                      <Wifi className="w-2.5 h-2.5 flex-shrink-0 text-purple-400" />
                                      <span className="text-[11px] truncate">{cam.name}</span>
                                    </DropdownItem>
                                  ))}
                                </>
                              ) : null;
                            })()}
                          </div>
                        )}
                      </div>

                      {/* ── Live preview ────────────────────────────── */}
                      <div
                        title={hasSource ? 'Clique para capturar' : undefined}
                        className={cn(
                          'relative w-full aspect-[3/2] rounded-lg overflow-hidden flex-shrink-0',
                          hasSource ? 'cursor-pointer ring-1 ring-blue-500/30 hover:ring-blue-500 group' : (darkMode ? 'bg-gray-700/30' : 'bg-gray-200/60'),
                        )}
                        onClick={() => hasSource && capturePreview(col.type)}
                      >
                        {isCircle ? (
                          /* circle: inscribed square centered in the 3:2 box */
                          <>
                            <div className={cn('absolute inset-0', darkMode ? 'bg-gray-800' : 'bg-gray-200')} />
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 h-full aspect-[4/5] rounded-xl overflow-hidden">
                              {source.kind === 'webcam' && <video ref={el => { videoRefs.current.set(col.type, el); if (el) videoElCache.current.set(col.type, el); }} autoPlay muted playsInline className="w-full h-full object-cover bg-black" />}
                              {source.kind === 'ipcamera' && (
                                mjpegUrl
                                  ? <MjpegLivePreview url={mjpegUrl} className="w-full h-full" compact />
                                  : framePreview
                                    ? (
                                        <div className="relative w-full h-full">
                                          <img src={framePreview} alt="preview" className="w-full h-full object-cover" />
                                          <div className="absolute top-1 left-1 flex items-center gap-0.5 bg-red-600/90 backdrop-blur-sm text-white font-semibold rounded-full px-1 py-0.5 text-[7px] pointer-events-none">
                                            <span className="w-1 h-1 bg-white rounded-full animate-pulse flex-shrink-0" />
                                            AO VIVO
                                          </div>
                                        </div>
                                      )
                                    : <div className={cn('w-full h-full flex items-center justify-center gap-1 flex-col', darkMode ? 'bg-gray-700' : 'bg-gray-100')}><RefreshCw className="w-3.5 h-3.5 text-gray-400 animate-spin" /><span className="text-[8px] text-gray-400">Conectando...</span></div>
                              )}
                              {source.kind === 'none' && <div className={cn('w-full h-full flex items-center justify-center', darkMode ? 'bg-gray-700/60' : 'bg-gray-200')}><Camera className={cn('w-5 h-5', darkMode ? 'text-gray-600' : 'text-gray-400')} /></div>}
                            </div>
                            {!hasSource && <span className={cn('absolute bottom-1 left-0 right-0 text-center text-[9px]', darkMode ? 'text-gray-600' : 'text-gray-400')}>Selecione fonte</span>}
                          </>
                        ) : (
                          /* rect: fills the container */
                          <>
                            {source.kind === 'webcam' && <video ref={el => { videoRefs.current.set(col.type, el); if (el) videoElCache.current.set(col.type, el); }} autoPlay muted playsInline className="absolute inset-0 w-full h-full object-cover bg-black" />}
                            {source.kind === 'ipcamera' && (
                              mjpegUrl
                                ? <MjpegLivePreview url={mjpegUrl} className="absolute inset-0" />
                                : framePreview
                                  ? (
                                      <>
                                        <img src={framePreview} alt="preview" className="absolute inset-0 w-full h-full object-cover" />
                                        <div className="absolute top-1.5 left-1.5 flex items-center gap-0.5 bg-red-600/90 backdrop-blur-sm text-white font-semibold rounded-full px-1.5 py-0.5 text-[9px] pointer-events-none">
                                          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse flex-shrink-0" />
                                          AO VIVO
                                        </div>
                                      </>
                                    )
                                  : <div className={cn('absolute inset-0 flex flex-col items-center justify-center gap-1', darkMode ? 'bg-gray-800' : 'bg-gray-100')}><RefreshCw className="w-4 h-4 text-gray-400 animate-spin" /><span className="text-[9px] text-gray-400">Conectando...</span></div>
                            )}
                            {source.kind === 'none' && <div className="absolute inset-0 flex flex-col items-center justify-center gap-1"><Camera className={cn('w-5 h-5', darkMode ? 'text-gray-600' : 'text-gray-400')} /><span className={cn('text-[9px]', darkMode ? 'text-gray-600' : 'text-gray-400')}>Selecione fonte</span></div>}
                          </>
                        )}
                        {hasSource && (
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex flex-col items-center justify-center gap-1 pointer-events-none">
                            <Camera className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
                            <span className="text-[10px] font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow">Capturar</span>
                          </div>
                        )}
                      </div>

                      {/* ── Current photo ──────────────────────────── */}
                      {photo ? (
                        <>
                          <div
                            className={cn(
                              'relative w-full aspect-[3/2] rounded-lg overflow-hidden flex-shrink-0 cursor-pointer group',
                              isCircle ? (darkMode ? 'bg-gray-800' : 'bg-gray-200') : '',
                            )}
                            onClick={() => setZoomUrl(photo.base64)}
                          >
                            {isCircle ? (
                              <div className="absolute top-0 left-1/2 -translate-x-1/2 h-full aspect-[4/5] rounded-xl overflow-hidden">
                                <img src={photo.base64} alt={col.label} className="w-full h-full object-cover" />
                              </div>
                            ) : (
                              <img src={photo.base64} alt={col.label} className="absolute inset-0 w-full h-full object-cover" />
                            )}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
                              <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                          {/* Action buttons below photo */}
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => openEditor(col.type, photo.base64)}
                              className={cn(
                                'flex-1 flex items-center justify-center gap-1 py-1 rounded-lg text-[10px] font-semibold tracking-wide border transition-colors',
                                darkMode ? 'border-blue-800 hover:bg-blue-900/40 text-blue-400' : 'border-blue-200 hover:bg-blue-50 text-blue-600',
                              )}
                            >
                              ✎ EDITAR
                            </button>
                            <button
                              type="button"
                              onClick={() => setCaptured(p => ({ ...p, [col.type]: null }))}
                              className={cn(
                                'flex-1 flex items-center justify-center gap-1 py-1 rounded-lg text-[10px] font-semibold tracking-wide border transition-colors',
                                darkMode ? 'border-red-900 hover:bg-red-900/40 text-red-400' : 'border-red-200 hover:bg-red-50 text-red-500',
                              )}
                            >
                              <X className="w-3 h-3" /> REMOVER
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className={cn(
                          'w-full aspect-[3/2] rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0',
                          darkMode ? 'bg-gray-700/30' : 'bg-gray-100/80',
                        )}>
                          <span className={cn('text-[9px]', darkMode ? 'text-gray-600' : 'text-gray-400')}>Sem foto</span>
                        </div>
                      )}

                      {/* ── File upload + paste ──────────────────── */}
                      <div className="flex-shrink-0 flex flex-col gap-1">
                        <input ref={el => fileInputs.current.set(col.type, el)} type="file" accept="image/*" className="hidden" onChange={e => handleFileChange(col.type, e)} />
                        <button
                          type="button"
                          onClick={() => fileInputs.current.get(col.type)?.click()}
                          className={cn(
                            'w-full flex items-center justify-center gap-1 py-1.5 rounded-lg text-[10px] font-semibold tracking-wide border transition-colors',
                            darkMode ? 'border-gray-600 hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'border-gray-300 hover:bg-gray-50 text-gray-500 hover:text-gray-700',
                          )}
                        >
                          <Upload className="w-3 h-3" />
                          ENVIAR ARQUIVO
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePaste(col.type)}
                          className={cn(
                            'w-full flex items-center justify-center gap-1 py-1.5 rounded-lg text-[10px] font-semibold tracking-wide border transition-colors',
                            darkMode ? 'border-gray-600 hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'border-gray-300 hover:bg-gray-50 text-gray-500 hover:text-gray-700',
                          )}
                        >
                          <Clipboard className="w-3 h-3" />
                          COLAR IMAGEM
                        </button>
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
        </div>

        {/* ── Footer (only in columns view) ──────────────────────────── */}
        <div className={cn('flex items-center justify-end gap-2 px-4 py-3 border-t flex-shrink-0', darkMode ? 'border-gray-700' : 'border-gray-200', editing && 'hidden')}>
            <button type="button" onClick={requestClose} className={cn('px-3 py-1.5 rounded-lg text-sm transition-colors', darkMode ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600')}>
              Cancelar
            </button>
            <button type="button" onClick={handleSave} className="px-4 py-1.5 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors">
              Salvar Fotos
            </button>
          </div>
      </div>
      </div>{/* end backdrop+centering wrapper */}

      {/* ── Zoom lightbox ──────────────────────────────────────────────── */}
      {zoomUrl && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[210] p-4" onClick={() => setZoomUrl(null)}>
          <div className="relative max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            <button type="button" onClick={() => setZoomUrl(null)} className="absolute -top-3 -right-3 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-lg text-gray-700">
              <X className="w-3.5 h-3.5" />
            </button>
            <img src={zoomUrl} alt="zoom" className="w-full rounded-xl shadow-2xl" />
          </div>
        </div>
      )}

      {/* Dropdown close is handled by a document click listener (see useEffect above) */}
    </>
  );
};

// ─── Small helpers ────────────────────────────────────────────────────────────

const DropdownSection: React.FC<{ darkMode?: boolean; children: React.ReactNode }> = ({ darkMode, children }) => (
  <div className={cn('px-2 pt-1.5 pb-0.5 text-[9px] font-semibold uppercase tracking-widest', darkMode ? 'text-gray-500' : 'text-gray-400')}>
    {children}
  </div>
);

const DropdownItem: React.FC<{ darkMode?: boolean; active?: boolean; onClick: () => void; children: React.ReactNode }> = ({ darkMode, active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'w-full px-2 py-1 text-left flex items-center gap-1.5 transition-colors',
      active
        ? darkMode ? 'bg-blue-700/40 text-blue-200' : 'bg-blue-50 text-blue-700'
        : darkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700',
    )}
  >
    {children}
  </button>
);

export default AllPhotosEditModal;
